import type { FunctionComponent } from 'react'
import styled from 'styled-components'
import useParam from '../../../../shared/hooks/useParam'
import { useMapContext } from '../../../../shared/contexts/map/MapContext'
import { mapLayersParam, zoomParam } from '../../query-params'
import { useIsEmbedded } from '../../../../shared/contexts/ui'
import useFuse from '../../../../shared/hooks/useFuse'
import LayerCollection from './LayerCollection'
import type { ExtendedMapGroup } from '../../../../shared/utils/map'
import SearchBar from '../../../../shared/components/SearchBar'
import useWindowWidth from '../../../../shared/hooks/useWindowWidth'
import type { MapGroup } from '../../../../types/mapTypes'
import { Breakpoint } from '../../../../shared/hooks/useBreakpoint'
import { DEFAULT_MAP_OPTIONS } from '../../../../shared/components/LeafletMap/LeafletMap'
import useLayerContext from '../../../../shared/contexts/Layers/useLayerContext'

const MapPanelWrapper = styled.div`
  margin-top: 1rem;
`

const LegendPanel = styled.div`
  @media screen and (max-width: 48rem) {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 1rem;
    height: auto;
  }
`

const MapLayersPanel: FunctionComponent = () => {
  const { activeEmbeddedMapLayers, setActiveEmbeddedMapLayers } = useMapContext()
  const { panelLayers } = useLayerContext()
  const [activeLayers, setActiveMapLayers] = useParam(mapLayersParam)
  const [zoom, setZoom] = useParam(zoomParam) ?? DEFAULT_MAP_OPTIONS.zoom
  const isEmbedded = useIsEmbedded()
  const { query, updateQuery, results } = useFuse(panelLayers, {
    keys: ['title', 'mapLayers.title', 'mapLayers.legendItems.title'],
    threshold: 0.2,
    findAllMatches: true,
    includeMatches: true,
    minMatchCharLength: 2,
  })

  const width = useWindowWidth()
  const isMobile = width < Breakpoint.Md.valueOf()

  const setMinZoomLevel = (layers: string[]) => {
    layers.forEach((layer) => {
      results.forEach(({ item }) => {
        const match = item?.mapLayers?.find((mapLayer: MapGroup) => mapLayer.id === layer)
        if (match?.minZoom && match.minZoom > zoom) {
          setZoom(match.minZoom)
        }
      })
    })
  }

  const onAddLayers = (layers: string[]) => {
    if (layers) {
      if (!isEmbedded) {
        setActiveMapLayers([...activeLayers, ...layers])
        if (isMobile) {
          setMinZoomLevel(layers)
        }
      } else {
        setActiveEmbeddedMapLayers([...activeEmbeddedMapLayers, ...layers])
      }
    }
  }

  const onRemoveLayers = (layers: string[]) => {
    // Check if map is embedded to avoid removing any query params
    if (!isEmbedded) {
      setActiveMapLayers(activeLayers.filter((layer) => !layers.includes(layer)))
    } else {
      setActiveEmbeddedMapLayers(activeEmbeddedMapLayers.filter((layer) => !layers.includes(layer)))
    }
  }

  return (
    <LegendPanel data-testid="legendPanel">
      {!isEmbedded && (
        <SearchBar
          placeholder="Zoeken op kaartlaag..."
          onClear={() => updateQuery('')}
          onChange={(e) => updateQuery(e.target.value)}
          value={query}
          name="mapLegendSearchBar"
          className="legend"
          iconSize={24}
        />
      )}

      <MapPanelWrapper data-testid="mapPanel" aria-label="Kaartlagen legenda">
        {results.map(({ item: { id, mapLayers, title }, matches }) => (
          <LayerCollection
            matches={matches}
            onAddLayers={onAddLayers}
            onRemoveLayers={onRemoveLayers}
            key={id}
            activeMapLayers={mapLayers as ExtendedMapGroup[]}
            title={title}
          />
        ))}
      </MapPanelWrapper>
    </LegendPanel>
  )
}

export default MapLayersPanel
